import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { ImageUploadField } from "../../shared/ImageUploadField";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  productReviewId: "",
  image: ""
});

export function ProductReviewImagesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadLookups = async () => {
    setLoading(true);
    setError(null);
    const rRes = await apiGet("/api/ProductReviews");
    if (rRes.ok) setReviews(rRes.data);
    else setError(rRes.error ?? "Failed to load product reviews");
    setLoading(false);
  };

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await apiGet(`/api/ProductReviewImages/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load review image");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        productReviewId: x.productReviewId ?? "",
        image: x.image ?? ""
      });
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSearchParams({}, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.productReviewId) return;
    setSaving(true);
    setError(null);
    const res = await apiPost("/api/ProductReviewImages", {
      id: editingId ?? 0,
      productReviewId: Number(form.productReviewId),
      image: form.image || null
    });
    if (!res.ok) setError(res.error ?? "Failed to save review image");
    else {
      await loadLookups();
      resetForm();
    }
    setSaving(false);
  };

  const onRefresh = async () => {
    setError(null);
    await loadLookups();
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/ProductReviewImages/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load review image");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        productReviewId: x.productReviewId ?? "",
        image: x.image ?? ""
      });
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Review Image" : "Add Review Image"}</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="submit"
            form="product-review-images-form"
            disabled={saving}
            title="Save"
            aria-label="Save review image"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/product-review-images-list" className="icon-button icon-button--toolbar" title="Review images list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>
      <form id="product-review-images-form" onSubmit={onSubmit} className="card-body form form-fields-bottomline">
        <label className="field">
          <span>Product Review</span>
          <select
            value={form.productReviewId}
            onChange={(e) => setForm((p) => ({ ...p, productReviewId: e.target.value ? Number(e.target.value) : "" }))}
          >
            <option value="">Select product review</option>
            {reviews.map((r) => (
              <option key={r.id} value={r.id}>
                Review #{r.id}
              </option>
            ))}
          </select>
        </label>
        <ImageUploadField label="Image Upload" value={form.image} onChange={(image) => setForm((p) => ({ ...p, image }))} />
        {error && <div className="error-banner">{error}</div>}
      </form>
    </div>
  );
}
